/**
 * Utilidades de formateo para números y precios en español
 */

import { AgeCategory, ActivityLevel } from '../types';

/** Display labels for age categories (Spanish) */
export const AGE_LABELS: Record<AgeCategory, string> = {
  [AgeCategory.CACHORRO]: 'Cachorro',
  [AgeCategory.ADULTO]: 'Adulto',
  [AgeCategory.SENIOR]: 'Senior',
};

/** Display labels for activity levels (Spanish) */
export const ACTIVITY_LABELS: Record<ActivityLevel, string> = {
  [ActivityLevel.BAJO]: 'Baja',
  [ActivityLevel.MEDIO]: 'Media',
  [ActivityLevel.ALTO]: 'Alta',
};

// Aliases en inglés que el backend serializa para algunos campos legacy
// (p.ej. life_stage devuelve 'puppy'/'adult'). Se mapean al valor canónico
// español antes de buscar el label.
const AGE_VALUE_ALIASES: Record<string, AgeCategory> = {
  puppy: AgeCategory.CACHORRO,
  adult: AgeCategory.ADULTO,
};

/**
 * Devuelve el label localizado para un valor de age_category.
 * Acepta tanto el enum canónico (cachorro/adulto/senior) como aliases en
 * inglés que aparecen en respuestas legacy. Si no encuentra match, devuelve
 * el valor crudo como fallback gráfico.
 */
export const getAgeLabel = (value: string | null | undefined): string => {
  if (!value) return '';
  const canonical = (AGE_VALUE_ALIASES[value] ?? value) as AgeCategory;
  return AGE_LABELS[canonical] ?? value;
};

/**
 * Formatea un número con separador de miles español (coma)
 * Ejemplo: 1049 → "1.049"
 */
export const formatNumber = (num: number): string => {
  return num.toLocaleString('es-ES', { useGrouping: true });
};

/**
 * Formatea un precio en euros con formato español
 * Ejemplo: 3.99 → "3,99€"
 */
export const formatPrice = (price: number): string => {
  return price.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€';
};

/**
 * Formatea gramos con unidad
 * Ejemplo: 400 → "400g"
 */
export const formatWeight = (grams: number): string => {
  return `${formatNumber(grams)}g`;
};

/**
 * Formatea una fecha ISO en formato corto español (DD MMM YYYY).
 * Devuelve cadena vacía si la fecha no es parseable.
 * Ejemplo: "2026-05-04T10:00:00Z" → "4 may 2026"
 */
export const formatShortDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
};

const CARD_BRAND_LABELS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'Amex',
  'american express': 'Amex',
  discover: 'Discover',
};

/** Devuelve un label legible para el brand de tarjeta. */
export const formatCardBrand = (brand: string | null | undefined): string => {
  if (!brand) return 'Tarjeta';
  return CARD_BRAND_LABELS[brand.trim().toLowerCase()] ?? brand;
};
